import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ExternalLink, ChevronRight } from 'lucide-react';
import {
  EditModeProvider,
  EditModeContext,
  AdminToolbar,
  EditableText
} from '../components/EditableContent';
import CMSBlockRenderer from '../components/CMSBlockRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function VendorsPageContent() {
  const { pageContent, setPageContent, editMode } = useContext(EditModeContext);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/vendors`)
      .then(res => setVendors(res.data.vendors || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getBlockContent = (type, defaultContent = {}) => {
    const block = pageContent?.blocks?.find(b => b.type === type);
    return block?.content || defaultContent;
  };

  const updateBlockContent = (type, updates) => {
    if (!pageContent) return;
    const blockIndex = pageContent.blocks?.findIndex(b => b.type === type);
    if (blockIndex >= 0) {
      const newBlocks = [...pageContent.blocks];
      newBlocks[blockIndex] = { ...newBlocks[blockIndex], content: { ...newBlocks[blockIndex].content, ...updates } };
      setPageContent({ ...pageContent, blocks: newBlocks });
    }
  };

  const heroContent = getBlockContent('hero', {
    title: 'Our Vendors',
    subtitle: 'We partner with industry-leading manufacturers to bring you the best wireless networking and communication solutions.',
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="vendors-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <EditableText
            value={heroContent.title || 'Our Vendors'}
            onChange={(val) => updateBlockContent('hero', { title: val })}
            tag="h1"
            className="text-4xl sm:text-5xl font-black text-white tracking-tight"
          />
          <EditableText
            value={heroContent.subtitle || 'We partner with industry-leading manufacturers to bring you the best wireless networking and communication solutions.'}
            onChange={(val) => updateBlockContent('hero', { subtitle: val })}
            tag="p"
            className="mt-3 text-zinc-400 text-lg max-w-2xl"
          />
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-8">
            {[1,2].map(i => <div key={i} className="h-64 bg-zinc-900 rounded-xl animate-pulse" />)}
          </div>
        ) : vendors.length === 0 ? (
          <p className="text-zinc-500 text-center py-16">No vendors available at this time.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {vendors.map(vendor => (
              <Link
                key={vendor.id}
                to={`/vendors/${vendor.slug}`}
                className="group block bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden hover:border-[#78BE20]/40 transition-all duration-300"
                data-testid={`vendor-card-${vendor.slug}`}
              >
                <div className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-white group-hover:text-[#78BE20] transition-colors">{vendor.name}</h2>
                    </div>
                    <div className="flex items-center gap-3">
                      {vendor.logo_url && (
                        <div className="h-8 max-w-[140px] shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
                          <img
                            src={vendor.logo_url}
                            alt={vendor.name}
                            className="h-full w-auto object-contain"
                            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      <ChevronRight className="h-5 w-5 text-zinc-600 group-hover:text-[#78BE20] group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                  <p className="text-zinc-400 text-sm leading-relaxed mb-6 line-clamp-3">{vendor.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-500 uppercase tracking-wider">
                      {vendor.resources?.length || 0} resource{(vendor.resources?.length || 0) !== 1 ? 's' : ''}
                    </span>
                    {vendor.website && (
                      <span className="text-xs text-[#78BE20]/60 flex items-center gap-1">
                        <ExternalLink className="h-3 w-3" /> {vendor.website.replace('https://', '').replace('http://', '')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="h-1 w-0 group-hover:w-full bg-[#78BE20] transition-all duration-500" />
              </Link>
            ))}
          </div>
        )}
      </main>
      <CMSBlockRenderer excludeTypes={['hero']} />
      <Footer />
      <AdminToolbar />
    </div>
  );
}

export default function VendorsPage() {
  return (
    <EditModeProvider pageSlug="vendors">
      <VendorsPageContent />
    </EditModeProvider>
  );
}
